import { remainingMs, SLEEP_FADE_MS, SLEEP_NUDGE_MS, SLEEP_PRESETS_MIN } from '../../domain/sleep';
import { translate } from '../../i18n';
import { clear, el } from '../../lib/dom';
import type { AppState } from '../../store/app';
import { formatClock } from '../format';

/**
 * Zaslon Časovnik spanja (P2 §5).
 *
 * Ko je časovnik nastavljen, `store/sleep.ts` vsako sekundo objavi svež
 * `sleep` in ta zaslon se izriše — zato tu ni lastnega intervala. Odštevanje
 * je `HH:MM:SS` v `.tnum` (§5.3), sicer cifre poskakujejo.
 *
 * Prednastavitve in ročni čas sta vedno vidna: klik nanju časovnik postavi ali
 * ponastavi. Aktivni panel z odštevanjem, ±5 min in preklicem se pokaže le, ko
 * teče.
 */

export interface TimerScreenDeps {
  onStart(durationMs: number): void;
  onCancel(): void;
  onNudge(deltaMs: number): void;
}

export function createTimerScreen(
  root: HTMLElement,
  deps: TimerScreenDeps,
): { update(state: AppState): void } {
  /* ── Aktivni panel: odštevanje + ±5 min + prekliči ───────────────── */
  const countdown = el('div', { class: 'nw-timer__count tnum', role: 'timer' });
  const statusLine = el('div', { class: 'nw-timer__status' });

  const minus = el('button', { class: 'btn secondary', type: 'button' });
  const plus = el('button', { class: 'btn secondary', type: 'button' });
  const cancel = el('button', { class: 'btn danger', type: 'button' });
  minus.addEventListener('click', () => {
    deps.onNudge(-SLEEP_NUDGE_MS);
  });
  plus.addEventListener('click', () => {
    deps.onNudge(SLEEP_NUDGE_MS);
  });
  cancel.addEventListener('click', deps.onCancel);

  const active = el('div', { class: 'nw-timer__active', hidden: '' }, [
    countdown,
    statusLine,
    el('div', { class: 'nw-timer__nudge' }, [minus, plus]),
    cancel,
  ]);

  /* ── Prednastavitve ──────────────────────────────────────────────── */
  const presetHeading = el('h2', { class: 'nw-section__title' });
  const presetButtons = SLEEP_PRESETS_MIN.map((mins) => {
    const button = el('button', { class: 'btn secondary nw-timer__preset', type: 'button' });
    button.addEventListener('click', () => {
      deps.onStart(mins * 60_000);
    });
    return { mins, button };
  });
  const presets = el(
    'div',
    { class: 'nw-timer__presets' },
    presetButtons.map((p) => p.button),
  );

  /* ── Ročni čas HH:MM ─────────────────────────────────────────────── */
  const customHeading = el('h2', { class: 'nw-section__title' });
  const hours = el('input', {
    class: 'input',
    type: 'number',
    id: 'timerHours',
    name: 'timerHours',
    min: '0',
    max: '23',
    inputmode: 'numeric',
    value: '0',
  });
  const minutes = el('input', {
    class: 'input',
    type: 'number',
    id: 'timerMinutes',
    name: 'timerMinutes',
    min: '0',
    max: '59',
    inputmode: 'numeric',
    value: '30',
  });
  const hoursLabel = el('label', { class: 'ftitle', for: 'timerHours' });
  const minutesLabel = el('label', { class: 'ftitle', for: 'timerMinutes' });
  const startCustom = el('button', { class: 'btn primary nw-timer__start', type: 'button' });

  function customMs(): number {
    const h = Math.max(0, Math.min(23, Math.floor(Number(hours.value) || 0)));
    const m = Math.max(0, Math.min(59, Math.floor(Number(minutes.value) || 0)));
    return (h * 60 + m) * 60_000;
  }

  function syncCustomState(): void {
    startCustom.disabled = customMs() < 60_000;
  }
  hours.addEventListener('input', syncCustomState);
  minutes.addEventListener('input', syncCustomState);
  startCustom.addEventListener('click', () => {
    const ms = customMs();
    if (ms >= 60_000) deps.onStart(ms);
  });

  const custom = el('div', { class: 'nw-timer__custom' }, [
    el('div', { class: 'field nw-timer__field' }, [hoursLabel, hours]),
    el('div', { class: 'field nw-timer__field' }, [minutesLabel, minutes]),
    startCustom,
  ]);

  clear(root);
  root.append(active, presetHeading, presets, customHeading, custom);

  return {
    update(state) {
      const { lang } = state;
      const sleep = state.sleep;

      cancel.textContent = translate(lang, 'timer.cancel');
      minus.textContent = translate(lang, 'timer.minus');
      plus.textContent = translate(lang, 'timer.plus');
      minus.setAttribute('aria-label', translate(lang, 'timer.minus.aria'));
      plus.setAttribute('aria-label', translate(lang, 'timer.plus.aria'));

      presetHeading.textContent = translate(lang, 'timer.presets.heading');
      for (const { mins, button } of presetButtons) {
        button.textContent = translate(lang, 'timer.preset', { n: mins });
      }

      customHeading.textContent = translate(lang, 'timer.custom.heading');
      hoursLabel.textContent = translate(lang, 'timer.hours');
      minutesLabel.textContent = translate(lang, 'timer.minutes');
      startCustom.textContent = translate(lang, 'timer.start');
      syncCustomState();

      if (sleep === null) {
        active.toggleAttribute('hidden', true);
        return;
      }

      const remaining = remainingMs(sleep, Date.now());
      active.toggleAttribute('hidden', false);
      countdown.textContent = formatClock(remaining);
      countdown.setAttribute(
        'aria-label',
        translate(lang, 'timer.remaining', { time: formatClock(remaining) }),
      );
      // Zadnjih 30 s: pove, da zvok pojema, ne le da čas teče (§5.8).
      statusLine.textContent = translate(
        lang,
        remaining <= SLEEP_FADE_MS ? 'timer.fading' : 'timer.active',
      );
    },
  };
}
